
import { Record, Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, float64_type, string_type, int32_type, union_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { item, setItem, fill } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { getTimeMs } from "../Common/TimeHelpers.js";
import { dbg } from "../Shared/Log.js";
import { printf, toText, join, interpolate } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { min } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { singleton, empty, collect, delay, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { map } from "../fable_modules/fable-library-js.5.13.0/List.js";

export class SimLogKind extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["SimBuild", "SimRun"];
    }
    static SimBuild = new SimLogKind(0, []);
    static SimRun = new SimLogKind(1, []);
}

export function SimLogKind_$reflection() {
    return union_type("SimLog.SimLogKind", [], SimLogKind, () => [[], []]);
}

export class SimLogRecord extends Record {
    constructor(Seq, Kind, Sheet, Components, FromCycle, ToCycle, Ms, At, PPct, Gen0, Gen1, Gen2, PauseMs, AllocMb, HeapMb) {
        super();
        this.Seq = (Seq | 0);
        this.Kind = Kind;
        this.Sheet = Sheet;
        this.Components = (Components | 0);
        this.FromCycle = (FromCycle | 0);
        this.ToCycle = (ToCycle | 0);
        this.Ms = Ms;
        this.At = At;
        this.PPct = PPct;
        this.Gen0 = (Gen0 | 0);
        this.Gen1 = (Gen1 | 0);
        this.Gen2 = (Gen2 | 0);
        this.PauseMs = PauseMs;
        this.AllocMb = AllocMb;
        this.HeapMb = HeapMb;
    }
}

export function SimLogRecord_$reflection() {
    return record_type("SimLog.SimLogRecord", [], SimLogRecord, () => [["Seq", int32_type], ["Kind", SimLogKind_$reflection()], ["Sheet", string_type], ["Components", int32_type], ["FromCycle", int32_type], ["ToCycle", int32_type], ["Ms", float64_type], ["At", float64_type], ["PPct", float64_type], ["Gen0", int32_type], ["Gen1", int32_type], ["Gen2", int32_type], ["PauseMs", float64_type], ["AllocMb", float64_type], ["HeapMb", float64_type]]);
}

function resetSamples() {
}

function samplePPct() {
    return -1;
}

function gcCounts() {
    return [0, 0, 0];
}

function gcPauseMs() {
    return 0;
}

function allocatedBytes() {
    return 0;
}

let atStart = [0, 0, 0, 0, 0];

/**
 * Bracket an invocation: called where its timing starts, so that GC and core samples cover
 * the same span as its Ms.
 */
export function beginInvocation() {
    resetSamples();
    const patternInput = gcCounts();
    atStart = [patternInput[0], patternInput[1], patternInput[2], gcPauseMs(), allocatedBytes()];
}

export const Constants_ringSize = 1000;

const ring = fill(new Array(Constants_ringSize), 0, Constants_ringSize, undefined);

let nextSeq = 0;

function kindName(kind) {
    if (kind.tag === 1) {
        return "run";
    }
    else {
        return "build";
    }
}

/**
 * Record one invocation. Called from inside the simulator; everything else reads.
 */
export function record(kind, sheet, components, fromCycle, toCycle, ms) {
    const sPause = atStart[3];
    const sAlloc = atStart[4];
    const s2 = atStart[2] | 0;
    const s1 = atStart[1] | 0;
    const s0 = atStart[0] | 0;
    const patternInput = gcCounts();
    const entry = new SimLogRecord(nextSeq, kind, sheet, components, fromCycle, toCycle, ms, getTimeMs(), samplePPct(), patternInput[0] - s0, patternInput[1] - s1, patternInput[2] - s2, gcPauseMs() - sPause, (allocatedBytes() - sAlloc) / 1048576, (typeof performance !== 'undefined' && performance.memory) ? performance.memory.usedJSHeapSize / 1048576 : 0);
    setItem(ring, nextSeq % Constants_ringSize, entry);
    nextSeq = ((nextSeq + 1) | 0);
    dbg(32, ((`sim ${kindName(kind)} ${sheet}: ${components} comps, cycles ${fromCycle}->${toCycle}, ${interpolate("%.2f%P()", [ms])}ms, `) + (`${interpolate("%.0f%P()", [entry.PPct])}% P-core, gc ${entry.Gen0}/${entry.Gen1}/${entry.Gen2} in ${interpolate("%.1f%P()", [entry.PauseMs])}ms, `)) + (`heap ${interpolate("%.0f%P()", [entry.HeapMb])}MB`));
}

/**
 * The recorded invocations, oldest first.
 */
export function recent() {
    const count = min(nextSeq, Constants_ringSize) | 0;
    const first = (nextSeq - count) | 0;
    return toList(delay(() => collect((i) => {
        const matchValue = item(i % Constants_ringSize, ring);
        if (matchValue == null) {
            return empty();
        }
        else {
            return singleton(matchValue);
        }
    }, rangeDouble(first, 1, nextSeq - 1))));
}

export function clear() {
    fill(ring, 0, ring.length, undefined);
    nextSeq = 0;
}

/**
 * The recorded invocations as a JSON array, hand-built so both runtimes emit byte-identical
 * shapes with no serializer in the way.
 */
export function recentJson() {
    const arg_15 = join(",", map((entry) => {
        const arg_1 = kindName(entry.Kind);
        return toText(printf("{\"seq\":%d,\"kind\":\"%s\",\"sheet\":\"%s\",\"components\":%d,\"fromCycle\":%d,\"toCycle\":%d,\"ms\":%.3f,\"at\":%.1f,\"pPct\":%.1f,\"gen0\":%d,\"gen1\":%d,\"gen2\":%d,\"pauseMs\":%.2f,\"allocMb\":%.2f,\"heapMb\":%.1f}"))(entry.Seq)(arg_1)(entry.Sheet)(entry.Components)(entry.FromCycle)(entry.ToCycle)(entry.Ms)(entry.At)(entry.PPct)(entry.Gen0)(entry.Gen1)(entry.Gen2)(entry.PauseMs)(entry.AllocMb)(entry.HeapMb);
    }, recent()));
    return toText(printf("[%s]"))(arg_15);
}

